import GlobalStyles from "@mui/material/GlobalStyles";
import type { Theme } from "@mui/material/styles";
import { SnackbarProvider as Provider } from "notistack";
import { PropsWithChildren, ReactElement, useEffect, useState } from "react";
import { useIsMediumScreen } from "../../../theming/useScreenSize";
import { ProgressToastContent, ToastContent } from "./toast-content";

const MAX_VISIBLE = 3;

// Below `md` a toast spans the viewport (docs/toast-alignment/PROPOSAL.md §3.3).
// Notistack only does that on `xs`, below 600 px, and styles its container from a
// static class list, so the breakpoint is widened here against its stable class names.
const fullWidthBelowMd = (theme: Theme) => ({
  [theme.breakpoints.down("md")]: {
    ".notistack-SnackbarContainer": {
      left: 16,
      right: 16,
      width: "auto",
      maxWidth: "none",
      alignItems: "stretch",
    },
    ".notistack-toast": { width: "100%" },
  },
});

/**
 * The one toast transport. Every toast, upload progress included, renders through
 * `ToastContent` under this provider; feature code never mounts a raw MUI toast.
 * Bottom-left, three visible, denser and full width below `md`. The stack follows
 * the fullscreen element so toasts stay visible while a preview is fullscreen.
 *
 * The stack is portalled to `<body>` rather than rendered in place: the app root is
 * an isolated stacking context (`#root { isolation: isolate }`, required by Base UI),
 * so a container inside it would paint under every body-level overlay — MUI's own
 * dialogs included — whatever its z-index. In `<body>` the stack's own z-index (1400)
 * sits above the MUI modals and the shadcn overlays as intended (`SrTheme.zIndex`).
 */
export const ToastProvider = ({ children }: PropsWithChildren<unknown>): ReactElement => {
  const [domRoot, setDomRoot] = useState<HTMLElement>(() => document.body);
  const dense = useIsMediumScreen();

  useEffect(() => {
    const syncDomRoot = () => {
      const fullscreenElement = document.fullscreenElement;
      setDomRoot(fullscreenElement instanceof HTMLElement ? fullscreenElement : document.body);
    };

    syncDomRoot();
    document.addEventListener("fullscreenchange", syncDomRoot);

    return () => {
      document.removeEventListener("fullscreenchange", syncDomRoot);
    };
  }, []);

  return (
    <>
      <GlobalStyles styles={fullWidthBelowMd} />
      <Provider
        domRoot={domRoot}
        maxSnack={MAX_VISIBLE}
        dense={dense}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        Components={{
          success: ToastContent,
          info: ToastContent,
          warning: ToastContent,
          error: ToastContent,
          progress: ProgressToastContent,
        }}
      >
        {children}
      </Provider>
    </>
  );
};
